import React, { ReactElement } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from '@emotion/styled';
import { BiTrash } from '@react-icons/all-files/bi/BiTrash';
import { BsGripVertical } from '@react-icons/all-files/bs/BsGripVertical';
import { DndWrapper, SelectField, TextInputField } from '@bucketplace/commerce-admin-ui';
import { Product } from '@bucketplace/common';
import { FONT_STYLE } from '@bucketplace/design-system';

import { BADGE_COLOR_OPTIONS } from '../constants';
import { ProductListProps } from '../ProductList';
import { ProductTextBadge, TextBadge } from '../types';
import { ItemAddModal } from './ItemAddModal';

export type ProductTextBadgeListProps = Pick<ProductListProps, 'type' | 'badgeType'> & {
  /** 상품 추가시 호출됩니다.
   * @param newProducts 뱃지와 합쳐진 상품 목록
   */
  onChange: (newProducts: ProductTextBadge[]) => void,
};

export function ProductTextBadgeList({
  type,
  badgeType,
  onChange,
}: ProductTextBadgeListProps): ReactElement {
  const { control, getValues } = useFormContext<{ products: ProductTextBadge[] }>();

  const {
    fields,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'products',
    keyName: 'key',
  });

  const handleAddItems = (items: Product[]): void => {
    const newItems = items.map((item) => ({
      id: item.id,
      name: item.name,
      textBadgeList: [
        { type: 'banner', color: '', value: '' },
        { type: 'circle', color: '', value: '' },
        { type: 'small', color: '', value: '', subIndex: 0 },
        { type: 'small', color: '', value: '', subIndex: 1 },
      ] as TextBadge[],
    })) as ProductTextBadge[];

    append(newItems);
  };

  const getMaxCount = (): number => {
    switch (badgeType) {
      case 'circle':
        return 9;
      case 'small':
        return 6;
      default:
        return 15;
    }
  };

  const ids = fields.map(({ id }) => {
    if (typeof id === 'number') {
      return id.toString();
    }
    return null;
  }).filter(Boolean) as string[];

  const getBadgeIndex = (textBadgeList: TextBadge[], subIndex?: number): number => {
    if (badgeType === 'small') {
      return textBadgeList.findIndex((badge) => badge.type === badgeType && badge.subIndex === subIndex);
    }
    return textBadgeList.findIndex((badge) => badge.type === badgeType);
  };

  return (
    <div>
      <AddItemModalWrapper>
        <ItemAddModal ids={ids} onAddItems={handleAddItems} />
      </AddItemModalWrapper>
      <List data-testid="product-inspector-list">
        { fields.map((item, index) => {
          const badges = item.textBadgeList.filter((badge) => badge.type === badgeType);
          const maximumBadges = [3, 'carousel'].includes(type) ? badges.slice(0, 1) : badges;

          return (
            <DndWrapper
              key={`${item.id}_${index + 1}`}
              id={index.toString()}
              onMoveItem={(sourceId, destinationId) => move(Number(sourceId), Number(destinationId))}
            >
              <Item>
                <TitleContainer>
                  <IconButton>
                    <BsGripVertical />
                  </IconButton>
                  <ProductId>
                    <em>{ `# ${item.id}` }</em>
                    <TextInputField
                      name={`products.${index}.name`}
                      width="100%"
                      onBlur={() => {
                        onChange(getValues('products'));
                      }}
                    />
                  </ProductId>
                  <IconButton data-testid="product-remove-button" onClick={() => remove(index)}>
                    <BiTrash />
                  </IconButton>
                </TitleContainer>
                <BadgeContainer data-testid="product-badge-list">
                  { maximumBadges.map((_, subIndex) => {
                    const colorName = `products.${index}.textBadgeList.${getBadgeIndex(item.textBadgeList, subIndex)}.color`;
                    const valueName = `products.${index}.textBadgeList.${getBadgeIndex(item.textBadgeList, subIndex)}.value`;

                    return (
                      <BadgeFormGroup key={`${subIndex + 1}`}>
                        <SelectField
                          key={colorName}
                          name={colorName}
                          width={120}
                          options={[
                            { text: '뱃지 컬러', value: '' },
                            ...BADGE_COLOR_OPTIONS,
                          ]}
                          onValue={() => {
                            onChange(getValues('products'));
                          }}
                        />
                        <TextInputField
                          key={valueName}
                          name={valueName}
                          width="100%"
                          maxCount={getMaxCount()}
                          onBlur={() => {
                            onChange(getValues('products'));
                          }}
                        />
                      </BadgeFormGroup>
                    );
                  }) }
                </BadgeContainer>
              </Item>
            </DndWrapper>
          );
        }) }
      </List>
    </div>
  );
}

const AddItemModalWrapper = styled.div`
  margin-left: 15px;
`;

const List = styled.ul`
  height: 1000px;
  overflow-y: auto;
  padding: 15px;
  margin-bottom: 0;
`;

const Item = styled.li`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.divider};
  padding: 5px 0;
`;

const TitleContainer = styled.ul`
  display: flex;
  align-items: center;
  > div {
    flex: 1;
    input {
      width: 100%;
    }
  }
  > button:first-of-type {
    cursor: move;
  }
`;

const ProductId = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  color: ${({ theme }) => theme.base.neutral600};

  & em {
    ${FONT_STYLE.body14L20};
    min-width: fit-content;
    font-weight: 700;
    color: ${({ theme }) => theme.secondary.basic};
    margin-right: 15px;
  }
  > div {
    width: 100%;
  }
`;

const IconButton = styled.button`
  display: flex;
  padding: 4px;
  border-radius: 50%;
  font-size: 18px;
  color: ${({ theme }) => theme.base.neutral400};

  &:hover {
    color: ${({ theme }) => theme.base.neutral500};
  }
`;

const BadgeContainer = styled.div`
  margin-top: 4px;
`;

const BadgeFormGroup = styled.div`
  display: flex;
  > div:last-of-type {
    width: 100%;
    margin-left: 8px;
  }
`;
